import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { applicantRequirementsApi } from '../applicantsApi';
import type { ApplicantRequirementListItem } from '../applicantTypes';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { sampleApplicantRequirement } from '../utils/sampleRecruitment';

/** Create or edit one applicant requirement — a line of the screening checklist. */
export function ApplicantRequirementModal({
  editing,
  onClose,
  onSaved,
}: {
  /** Null creates. */
  editing: ApplicantRequirementListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  // Seeded once, on mount — the parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [requiresAttachment, setRequiresAttachment] = useState(editing?.requiresAttachment ?? false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = name.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { name: name.trim(), description: description.trim() || null, requiresAttachment };

    try {
      if (editing) await applicantRequirementsApi.update(editing.id, request);
      else await applicantRequirementsApi.create(request);

      toast.success(editing ? `Saved ${request.name}.` : `Added ${request.name}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the requirement.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'applicant-requirement' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit applicant requirement' : 'New applicant requirement'}
      size="lg"
      id="applicant-requirement-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleApplicantRequirement();
          setName(sample.name);
          setDescription(sample.description ?? '');
          setRequiresAttachment(sample.requiresAttachment);
        },
        disabled: saving,
      }}
      confirm={{ id: 'applicant-requirement-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="applicant-requirement-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="applicant-requirement-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="applicant-requirement-name"
          label="Name"
          required
          description="What the applicant submits, e.g. Personal Data Sheet. Held once among the live rows, and ticked off at initial screening."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <TextField
          id="applicant-requirement-description"
          label="Description"
          description="Optional — shown under the requirement on the screening checklist."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('description')}
        />

        {/* A checkbox for a CONFIGURATION that defaults off — its unticked state asserts nothing. */}
        <label htmlFor="applicant-requirement-requires-attachment" className="flex items-start gap-2 text-sm text-slate-600">
          <Checkbox
            id="applicant-requirement-requires-attachment"
            checked={requiresAttachment}
            onChange={(e) => setRequiresAttachment(e.target.checked)}
            disabled={saving}
          />
          <span>
            Requires an attachment
            <span className="block text-xs text-slate-500">
              At initial screening, Complied? can be Yes only once a file is attached to this requirement.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
